'use client'

import React, { useState } from "react";
import { useTableMapState } from "../TableMapState";
import { formatValue } from "@/migrated-widgets/table-map/utils/random";
import { Make, Model } from "../types";
import { InventoryRow, InventoryCell, InventoryExpander } from "./components";

const ByModelsView: React.FC = () => {
  const {
    setVehicleTypes, setMakes, setModels, setZipCodes,
    zipCodes: activeZipCodes, vehicleTypes: activeVehicleTypes,
    models: activeModels, makes: activeMakes,
    totalSales, totalLeads, showCrmLeads, triggerZoomReset,
  } = useTableMapState();

  const [isExpanded, setIsExpanded] = useState(new Map<string, boolean>());

  const handleSelectMake = (make: Make) => {
    if (activeZipCodes.length > 0) return;
    setMakes((prev) => {
      if (prev.includes(make.name)) return prev.filter(el => el !== make.name);
      return [...prev, make.name];
    });
  };

  const handleUnselectMake = (make: Make) => {
    setMakes(prev => prev.filter(el => el !== make.name));
  };

  const handleSelectModel = (model: Model) => {
    if (activeZipCodes.length > 0) return;
    setModels((prev) => {
      if (prev.includes(model.name)) return prev.filter(el => el !== model.name);
      return [...prev, model.name];
    });
  };

  const handleUnselectModel = (model: Model) => {
    setModels(prev => prev.filter(el => el !== model.name));
  };

  const handleSelectVehicleType = (type: "NEW" | "USED") => {
    if (activeZipCodes.length > 0) return;
    setVehicleTypes((prev) => {
      if (prev.includes(type)) return prev.filter(el => el !== type);
      return [...prev, type];
    });
  };

  const isActiveVehicleType = (type: "NEW" | "USED") => activeVehicleTypes.includes(type);
  const isActiveMake = (make: string) => activeMakes.includes(make);
  const isActiveModel = (model: string) => activeModels.includes(model);

  const resetAllFilters = () => {
    if (activeZipCodes.length > 0) setZipCodes([]);
    if (activeVehicleTypes.length > 0) setVehicleTypes([]);
    if (activeMakes.length > 0) setMakes([]);
    if (activeModels.length > 0) setModels([]);
    triggerZoomReset();
  };

  const resetFilters = () => {
    if (activeZipCodes.length > 0) return;
    resetAllFilters();
  };

  const handleExpand = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    isExpanded.set(key, !isExpanded.get(key));
    setIsExpanded(new Map(isExpanded));
  };

  const getExpanded = (key: string) => {
    if (activeZipCodes.length) return true;
    return isExpanded.get(key);
  };

  return (
    <>
      <InventoryRow className="sticky z-10 top-0 bg-[#f9fafa] font-bold">
        <InventoryCell>Inventory Type</InventoryCell>
        <InventoryCell>Amount</InventoryCell>
      </InventoryRow>

      <InventoryRow
        isHighlighted={!activeZipCodes.length && !activeModels.length && !activeMakes.length && isActiveVehicleType("NEW")}
        useCursorPointer={!activeZipCodes.length}
        onClick={() => handleSelectVehicleType("NEW")}
      >
        <InventoryCell>
          <InventoryExpander onClick={(e: React.MouseEvent) => handleExpand(e, "NEW")} isExpanded={getExpanded("NEW")} />New
        </InventoryCell>
        <InventoryCell>{formatValue(totalSales.newSold)}</InventoryCell>
      </InventoryRow>

      {getExpanded("NEW") && totalSales.newSales.map((make) => (
        <React.Fragment key={make.name}>
          <InventoryRow
            className="pl-14 bg-table-map-lightGray/20"
            isHighlighted={!activeZipCodes.length && !activeModels.length && isActiveMake(make.name)}
            useCursorPointer={!activeZipCodes.length}
            onClick={() => handleSelectMake(make)}
            onUnmount={() => handleUnselectMake(make)}
          >
            <InventoryCell>
              <InventoryExpander isExpanded={getExpanded(make.name)} onClick={(e: React.MouseEvent) => handleExpand(e, make.name)} />{make.name}
            </InventoryCell>
            <InventoryCell>{formatValue(make.totalSold)}</InventoryCell>
          </InventoryRow>

          {getExpanded(make.name) && make.models.map((model) => (
            <InventoryRow
              key={model.name}
              className="pl-20 bg-table-map-lightGray/50"
              isHighlighted={(!activeZipCodes.length && isActiveModel(model.name)) || (activeZipCodes.length > 0 && model.totalSold > 0)}
              useCursorPointer={!activeZipCodes.length}
              onClick={() => handleSelectModel(model)}
              onUnmount={() => handleUnselectModel(model)}
            >
              <InventoryCell>{model.name}</InventoryCell>
              <InventoryCell>{formatValue(model.totalSold)}</InventoryCell>
            </InventoryRow>
          ))}
        </React.Fragment>
      ))}

      <InventoryRow
        isHighlighted={(!activeZipCodes.length && !activeModels.length && !activeMakes.length && isActiveVehicleType("USED")) || (activeZipCodes.length > 0 && totalSales.usedSold > 0)}
        useCursorPointer={!activeZipCodes.length}
        onClick={() => handleSelectVehicleType("USED")}
      >
        <InventoryCell className="pl-5">Used</InventoryCell>
        <InventoryCell>{formatValue(totalSales.usedSold)}</InventoryCell>
      </InventoryRow>

      <InventoryRow
        className="font-bold"
        isHighlighted={!activeZipCodes.length && !activeVehicleTypes.length && !activeModels.length && !activeMakes.length}
        useCursorPointer={!activeZipCodes.length}
        onClick={resetFilters}
      >
        <InventoryCell className="pl-5">Total</InventoryCell>
        <InventoryCell>{formatValue(totalSales.totalSold)}</InventoryCell>
      </InventoryRow>

      {showCrmLeads && (
        <InventoryRow className="font-bold">
          <InventoryCell className="pl-5 text-[#007BFF]">CRM Leads</InventoryCell>
          <InventoryCell className="text-[#007BFF]">{formatValue(totalLeads)}</InventoryCell>
        </InventoryRow>
      )}
    </>
  );
};

export default ByModelsView;
